import { authOptions } from '@/app/api/auth/[...nextauth]/config';
import { TypedDocumentNode } from '@apollo/client';
import { Severity } from '@treyd-io/core/constants/logging';
import { REQUEST_METHOD } from '@treyd-io/core/constants/request';
import { RequestOptions } from '@treyd-io/core/types/requests';
import {
  get as defaultGet,
  save as defaultSave,
} from '@treyd-io/core/utils/request';
import axios, { AxiosResponse } from 'axios';
import { getServerSession } from 'next-auth';
import { getSession } from 'next-auth/react';

export const log = async <ResponseDataType>({
  res,
  options,
  severity,
}: {
  res: AxiosResponse<ResponseDataType>;
  options: RequestOptions;
  severity?: Severity;
}) => {
  const isServerEnvironment = typeof window === 'undefined';
  const session = isServerEnvironment
    ? await getServerSession()
    : await getSession();

  return axios.request<ResponseDataType>({
    baseURL: '/api',
    url: '/log',
    method: 'POST',
    headers: {
      ...options.headers,
    },
    data: {
      ...res,
      additionalData: {
        severity,
        correlationId: session?.correlationId,
        userId: session?.user?.id,
        companyId: session?.user?.company_id,
      },
    },
  });
};

export const save = async <T>({
  url,
  data,
  method,
  options = {},
}: {
  url: string;
  data?: unknown;
  method: REQUEST_METHOD;
  options?: RequestOptions;
}) => {
  if (!options.noAuth) {
    const isServerEnvironment = typeof window === 'undefined';
    const session = isServerEnvironment
      ? await getServerSession()
      : await getSession();

    if (session?.user?.accessToken)
      options.headers = {
        Authorization: `Bearer ${session.user.accessToken}`,
        AUTHORIZATIONJWT: `Bearer ${session.user.accessToken}`,
        'X-CORRELATION-ID': `${session.correlationId}`,
      };
  }
  return defaultSave<T>(url, data, method, options);
};

export const get = async <T>({
  url,
  options = {},
}: {
  url: string;
  options?: RequestOptions;
}) => {
  if (!options.noAuth) {
    const isServerEnvironment = typeof window === 'undefined';
    const session = isServerEnvironment
      ? await getServerSession(authOptions())
      : await getSession();

    if (session?.user?.accessToken)
      options.headers = {
        Authorization: `Bearer ${session.user.accessToken}`,
        AUTHORIZATIONJWT: `Bearer ${session.user.accessToken}`,
        'X-CORRELATION-ID': `${session.correlationId}`,
      };
  }
  return defaultGet<T>(url, options);
};

export const proxy = async <
  TResult,
  TVariables = Record<string, unknown>,
>(data: {
  query?: TypedDocumentNode<TResult, TVariables>;
  mutation?: TypedDocumentNode<TResult, TVariables>;
  variables?: TVariables;
}) =>
  save<TResult>({
    options: {
      noAuth: true,
      baseURL: '/api/graphql',
    },
    url: '/proxy',
    method: REQUEST_METHOD.POST,
    data,
  });
